import { Pipe, PipeTransform } from '@angular/core';

/**
 * Minimal Markdown → HTML for Atlas answers.
 *
 * Deliberately not a full CommonMark implementation — it covers what the model
 * actually emits (headings, bold/italic, lists, tables, code, links) and nothing else.
 *
 * SAFETY: the output is bound with plain [innerHTML], NOT bypassSecurityTrustHtml,
 * so Angular's sanitizer still runs over it and drops scripts, event handlers and
 * javascript: URLs. Everything here escapes HTML up front as a second layer.
 */
@Pipe({ name: 'atlasMarkdown', standalone: true })
export class AtlasMarkdownPipe implements PipeTransform {
  transform(value: string | null | undefined): string {
    return render(value ?? '');
  }
}

const FENCE = ' F';
const TICK = ' T';

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function render(source: string): string {
  const fences: string[] = [];
  const ticks: string[] = [];

  let text = escapeHtml(source.replace(/\r\n?/g, '\n'));

  // Pull code out first so its contents never get treated as markup.
  text = text.replace(/```[^\n]*\n?([\s\S]*?)```/g, (_m, code: string) => {
    fences.push(code.replace(/\n$/, ''));
    return `\n${FENCE}${fences.length - 1} \n`;
  });
  text = text.replace(/`([^`\n]+)`/g, (_m, code: string) => {
    ticks.push(code);
    return `${TICK}${ticks.length - 1} `;
  });

  const html = parseBlocks(text.split('\n'));

  return html
    .replace(new RegExp(`${TICK}(\\d+) `, 'g'), (_m, n: string) => `<code>${ticks[+n]}</code>`)
    .replace(new RegExp(`${FENCE}(\\d+) `, 'g'), (_m, n: string) => `<pre><code>${fences[+n]}</code></pre>`);
}

function parseBlocks(lines: string[]): string {
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    // Extracted fenced code sits alone on its line — emit it as its own block.
    if (new RegExp(`^${FENCE}\\d+ $`).test(line.trim())) {
      out.push(line.trim());
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length + 2, 6); // #  → h3, keeps it inside a chat bubble
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push('<hr>');
      i++;
      continue;
    }

    // Table: a row of pipes followed by a |---|---| separator.
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]*-[\s:|-]*$/.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      const head = header.map((c) => `<th>${inline(c)}</th>`).join('');
      const body = rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('');
      out.push(`<div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`);
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*+]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+[.)]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // Paragraph — runs until a blank line or the start of another block.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i])) {
      para.push(lines[i].trim());
      i++;
    }
    out.push(`<p>${inline(para.join('\n')).replace(/\n/g, '<br>')}</p>`);
  }

  return out.join('');
}

function startsBlock(line: string): boolean {
  return (
    /^#{1,6}\s/.test(line) ||
    /^\s*[-*+]\s+/.test(line) ||
    /^\s*\d+[.)]\s+/.test(line) ||
    /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line) ||
    new RegExp(`^${FENCE}\\d+ $`).test(line.trim())
  );
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function inline(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}
